import React from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { FlatProduct } from "@/features/line-sheet-sets/helpers/sheet-state";
import { LineSheetProductMarketPrice } from "@/features/order-sheet-sets/hooks/use-line-sheet-set-market-prices";
import {
  EstimatedSupplyBreakdown,
  toMarketCell,
} from "@/features/order-sheet-sets/utils/order-sheet-market-product";
import EstimatedSupplyTooltip from "@/features/order-sheet-sets/order-sheet-estimated-supply-tooltip";
import LineSheetProductMarketPriceLine, {
  MARKET_MUTED,
} from "@/features/order-sheet-sets/order-sheet-market-price-line";
import { OpenMarketExternal } from "@/features/order-sheet-sets/order-sheet-market-external-link";

/** 셀이 그리드 밖의 최신 시세를 읽는 통로 — colDefs 가 첫 렌더의 클로저를 붙잡아서 ref 로 넘긴다. */
export interface LineSheetProductMarketPriceControl {
  get: (productId: number) => LineSheetProductMarketPrice | undefined;
  // 예상 매입가와 계산 재료 — 그 상품의 '지금' 공급가로 계산한다(컨디션을 고치면 바뀐다).
  // 재료까지 돌려주는 건 값에 올렸을 때 계산식을 보여주기 위해서다.
  getEstimate: (productId: number) => EstimatedSupplyBreakdown | null;
  openSizeDetail: (productId: number) => void;
  // 마켓 사이트로 나가기 — 크림은 바로, 포이즌은 안내 모달을 거친다(그리드가 모달을 하나 쥔다).
  openExternal: OpenMarketExternal;
}

interface Props extends ICellRendererParams<FlatProduct> {
  control: LineSheetProductMarketPriceControl;
}

// 그리드 행 높이는 25px(셀 안쪽 24px)이다. 높이를 %로 두면 ag-grid 가 셀을 감싸는 방식에 따라
// 풀리지 않아 내용이 위로 붙는다 — 같은 그리드의 다른 셀(OverlappedValueCell)처럼 24px 로 못 박고
// 그 안에서 세로 가운데로 맞춘다. 줄 높이도 normal 로 되돌려 글자 상자가 행 높이만큼 커지지 않게 한다.
const CELL_HEIGHT = "24px";
const LINE_PROPS = {
  height: CELL_HEIGHT,
  alignItems: "center",
  lineHeight: "normal",
  fontSize: "11px",
} as const;

/**
 * 오더시트 시세 열. 상품 한 개가 3줄이라 줄마다 다른 것을 그린다 —
 *   0: 예상 매입가(원화 — 지금 공급가에 패스픽 핫딜과 같은 식을 적용한 값, 올리면 계산식)
 *      + 사이즈별 보기,
 *   1: KREAM 예상 마진 금액 + 최저 입찰가, 2: POIZON 예상 마진 금액 + 최저 입찰가.
 * 마진은 0 줄의 예상 매입가로 계산한다. 1·2 줄의 가격을 누르면 그 마켓으로 나간다.
 */
export default function LineSheetProductMarketPriceCell({
  data,
  node,
  control,
}: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const rowIndex = node?.rowIndex ?? -1;
  const productId = data?.id;
  // 요약·안내 행(음수 id)이나 필터 밖 행에는 시세를 붙이지 않는다.
  if (productId == null || productId < 0 || rowIndex < 0) {
    return null;
  }
  const marketPrice = control.get(productId);
  const estimate = control.getEstimate(productId);
  const estimatedKrw = estimate?.value ?? null;
  const line = rowIndex % 3;

  if (line === 0) {
    if (!marketPrice) {
      return null;
    }
    return (
      <Flex {...LINE_PROPS} justifyContent={"space-between"} gap={"4px"}>
        <Text
          as={"button"}
          type={"button"}
          color={"#1272EF"}
          lineHeight={"normal"}
          textDecoration={"underline"}
          whiteSpace={"nowrap"}
          onClick={(e) => {
            e.stopPropagation();
            control.openSizeDetail(productId);
          }}
        >
          {t("market_price_by_size")}
        </Text>
        {estimatedKrw != null ? (
          <EstimatedSupplyTooltip breakdown={estimate}>
            <Text
              color={"#303136"}
              fontWeight={700}
              fontSize={"12px"}
              cursor={"help"}
            >
              {tCurrencyString(estimatedKrw, { type: "SYMBOL", code: "KRW" })}
            </Text>
          </EstimatedSupplyTooltip>
        ) : (
          <Text color={MARKET_MUTED}>-</Text>
        )}
      </Flex>
    );
  }

  return (
    <Flex {...LINE_PROPS} justifyContent={"flex-end"}>
      <LineSheetProductMarketPriceLine
        cell={toMarketCell(
          marketPrice,
          line === 1 ? "KREAM" : "POIZON",
          estimatedKrw
        )}
        onOpen={control.openExternal}
      />
    </Flex>
  );
}
